// Per-dog verbal cues manager. Lives on the Assessment & Training page.
// Users pick from a preset list (or type a custom cue name), assign a category
// (Command / Signal / Trick / Other), and jot down how *they* personally use
// the cue with this dog.
import { useCallback, useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { SectionTitle } from "@/components/app/Bits";
import { Trash2, Plus, MessageSquareQuote } from "lucide-react";

// Curated starter list — user can extend with custom cue names.
const PRESET_CUES = [
  "Leave It",
  "No",
  "Yes",
  "Kitty Kitty Kitty",
  "Up",
  "Let's Go",
  "Stay/Wait",
  "Good",
  "Free",
  "Release",
  "All Done",
  "Bow",
  "Sit",
  "Up/Stand",
  "Up (Get up)",
  "Paws (Get up)",
  "Back",
  "Come",
  "Ready, ready, ready",
  "Clicker",
  "Cluck/click/kiss",
  "Hup",
  "Trot on",
  "Trilling (BRRTTTT)",
  "Yeah, good (upward voice)",
];

const CATEGORIES = [
  { value: "command", label: "Command" },
  { value: "signal", label: "Signal" },
  { value: "trick", label: "Trick" },
  { value: "other", label: "Other" },
];

const CATEGORY_LABEL = Object.fromEntries(CATEGORIES.map((c) => [c.value, c.label]));

export default function VerbalCuesSection({ dogId, dogName }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    label: PRESET_CUES[0],
    customLabel: "",
    category: "command",
    usage_note: "",
  });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!dogId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/dogs/${dogId}/verbal-cues`);
      setItems(data.items || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not load cues");
    } finally {
      setLoading(false);
    }
  }, [dogId]);

  useEffect(() => { load(); }, [load]);

  const isCustom = form.label === "__custom__";

  const submit = async (e) => {
    e.preventDefault();
    const label = isCustom ? form.customLabel.trim() : form.label;
    if (!label) return;
    setSaving(true);
    try {
      await api.post(`/dogs/${dogId}/verbal-cues`, {
        label,
        category: form.category,
        usage_note: form.usage_note,
      });
      setForm({ label: PRESET_CUES[0], customLabel: "", category: "command", usage_note: "" });
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not save cue");
    } finally {
      setSaving(false);
    }
  };

  const updateUsageNote = async (id, value) => {
    // Optimistic local update; persist on blur to avoid a request per keystroke
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, usage_note: value } : it)));
  };

  const persistUsageNote = async (id, value) => {
    try {
      await api.put(`/dogs/${dogId}/verbal-cues/${id}`, { usage_note: value });
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not save note");
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/dogs/${dogId}/verbal-cues/${id}`);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not delete");
    }
  };

  return (
    <div className="space-y-4" data-testid="verbal-cues-section">
      <SectionTitle
        overline="Shared language"
        title="Verbal cues"
      />
      <p className="text-sm text-muted-foreground max-w-2xl -mt-2">
        Every household uses cues a little differently. Save the ones you use with{" "}
        <strong>{dogName}</strong> and note exactly how you use each — future you and any co-owner
        stay on the same page.
      </p>

      <Card className="border-border shadow-none rounded-lg">
        <CardContent className="p-6 space-y-4">
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="verbal-cue-form">
            <div>
              <Label htmlFor="vc-label">Cue</Label>
              <select
                id="vc-label"
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                className="mt-1.5 w-full border border-input rounded-md bg-card px-3 py-2 text-sm"
                data-testid="verbal-cue-select"
              >
                {PRESET_CUES.map((c) => <option key={c} value={c}>{c}</option>)}
                <option value="__custom__">+ Custom cue name…</option>
              </select>
              {isCustom ? (
                <Input
                  value={form.customLabel}
                  onChange={(e) => setForm({ ...form, customLabel: e.target.value })}
                  placeholder="Type your cue name"
                  maxLength={60}
                  className="mt-2"
                  data-testid="verbal-cue-custom-name"
                />
              ) : null}
            </div>
            <div>
              <Label htmlFor="vc-category">Category</Label>
              <select
                id="vc-category"
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="mt-1.5 w-full border border-input rounded-md bg-card px-3 py-2 text-sm"
                data-testid="verbal-cue-category"
              >
                {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div className="md:col-span-2">
              <Label htmlFor="vc-note">How I use it (optional)</Label>
              <Textarea
                id="vc-note"
                rows={2}
                value={form.usage_note}
                onChange={(e) => setForm({ ...form, usage_note: e.target.value })}
                placeholder='e.g. "Leave It" = walk past whatever caught his eye, no lingering.'
                className="mt-1.5 text-sm"
                data-testid="verbal-cue-note"
              />
            </div>
            <div className="md:col-span-2">
              <Button
                type="submit"
                disabled={saving || (isCustom ? !form.customLabel.trim() : !form.label)}
                className="bg-primary text-primary-foreground hover:bg-primary/90"
                data-testid="verbal-cue-submit"
              >
                <Plus size={14} className="mr-1" /> {saving ? "Saving…" : "Add cue"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Saved cues list */}
      <div className="space-y-2" data-testid="verbal-cues-list">
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading cues…</p>
        ) : items.length === 0 ? (
          <Card className="border-dashed border-border shadow-none rounded-lg">
            <CardContent className="p-6 text-sm text-muted-foreground text-center">
              No cues yet — start with the ones you use every day.
            </CardContent>
          </Card>
        ) : (
          items.map((it) => (
            <Card
              key={it.id}
              className="border-border shadow-none rounded-lg"
              data-testid={`verbal-cue-item-${it.id}`}
            >
              <CardContent className="p-4 sm:p-5">
                <div className="flex items-start gap-3">
                  <div className="h-8 w-8 rounded-md bg-secondary text-secondary-foreground flex items-center justify-center shrink-0">
                    <MessageSquareQuote size={15} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-display font-bold text-base leading-tight">{it.label}</p>
                      <span className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground bg-secondary/60 rounded px-1.5 py-0.5">
                        {CATEGORY_LABEL[it.category] || "Other"}
                      </span>
                    </div>
                    <Textarea
                      rows={2}
                      value={it.usage_note || ""}
                      onChange={(e) => updateUsageNote(it.id, e.target.value)}
                      onBlur={(e) => persistUsageNote(it.id, e.target.value)}
                      placeholder="How you use this cue…"
                      className="mt-2 text-sm"
                      data-testid={`verbal-cue-usage-${it.id}`}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => remove(it.id)}
                    className="text-muted-foreground hover:text-destructive p-1 shrink-0"
                    title="Remove cue"
                    data-testid={`verbal-cue-remove-${it.id}`}
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
